// src/Component/MarketOverview.tsx
import React, { useEffect, useState, useRef } from 'react';
import { Repeat, Wallet, ArrowUpRight, ArrowDownRight, Activity, CheckCircle2, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

// Global declaration for TradingView
declare global {
  interface Window {
    TradingView: any;
  }
}

let tvScriptLoadingPromise: Promise<void> | null = null;

const MarketOverview: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'buy' | 'sell'>('buy');
  const [price, setPrice] = useState<number>(2950.50);
  const [quantity, setQuantity] = useState<string>("");
  const [inrBalance, setInrBalance] = useState<number>(550000.00);
  const [stockBalance, setStockBalance] = useState<number>(150);
  const [toast, setToast] = useState<{msg: string, type: 'success'|'error'} | null>(null);
  
  const widgetContainerRef = useRef<HTMLDivElement>(null);

  const totalInr = (parseFloat(quantity) || 0) * price;

  const handleTrade = () => {
    const qty = parseFloat(quantity);
    
    if (isNaN(qty) || qty <= 0) {
      showToast("Please enter a valid quantity to trade.", "error");
      return;
    }

    if (activeTab === 'buy') {
      if (totalInr > inrBalance) {
        showToast("Insufficient Funds in Trading Account!", "error");
        return;
      }
      setInrBalance(prev => prev - totalInr);
      setStockBalance(prev => prev + qty);
      showToast(`Successfully Bought ${qty} Shares!`, "success");
    } else {
      if (qty > stockBalance) {
        showToast("Insufficient Shares in Demat!", "error");
        return;
      }
      setStockBalance(prev => prev - qty);
      setInrBalance(prev => prev + totalInr);
      showToast(`Successfully Sold ${qty} Shares!`, "success");
    }
    
    setQuantity(""); 
  };

  const showToast = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500); 
  };

  // --- OPTIMIZED TRADINGVIEW INITIALIZATION ---
  useEffect(() => {
    const onLoadScriptRef = () => {
      if (typeof window.TradingView !== 'undefined' && widgetContainerRef.current) {
         // Clear previous content just in case
         widgetContainerRef.current.innerHTML = '';
         
         new window.TradingView.widget({
          autosize: true,
          // Using BSE symbol which is generally more stable on the free widget
          symbol: "BSE:RELIANCE", 
          interval: "D",
          timezone: "Asia/Kolkata", 
          theme: "dark",
          style: "1",
          locale: "in", 
          enable_publishing: false,
          backgroundColor: "rgba(11, 18, 21, 1)",
          gridColor: "rgba(30, 34, 45, 1)",
          hide_top_toolbar: false,
          hide_legend: false,
          save_image: false,
          container_id: "tradingview_chart_container", // Must match the div ID
          show_popup_button: true,
          popup_width: "1000",
          popup_height: "650"
        });
      }
    };

    if (!tvScriptLoadingPromise) {
      tvScriptLoadingPromise = new Promise((resolve) => {
        const script = document.createElement('script');
        script.id = 'tradingview-widget-loading-script';
        script.src = 'https://s3.tradingview.com/tv.js';
        script.type = 'text/javascript';
        script.onload = () => resolve();
        document.head.appendChild(script);
      });
    }

    tvScriptLoadingPromise.then(onLoadScriptRef);

    // Cleanup function is usually not needed for this script, but good practice
    return () => {
      if(widgetContainerRef.current) {
        widgetContainerRef.current.innerHTML = '';
      }
    }
  }, []);

  return (
    <section className="relative py-16 md:py-20 bg-black overflow-hidden min-h-screen flex flex-col justify-center font-[Urbanist]">
      
      {/* Toast Notification Popup */}
      <AnimatePresence>
        {toast && (
          <motion.div 
            initial={{ opacity: 0, y: -50, x: '-50%' }}
            animate={{ opacity: 1, y: 20, x: '-50%' }}
            exit={{ opacity: 0, y: -50, x: '-50%' }}
            className={`fixed top-0 left-1/2 z-50 flex items-center gap-3 px-6 py-4 rounded-xl shadow-2xl backdrop-blur-md border ${
              toast.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/50 text-emerald-400' : 'bg-rose-500/10 border-rose-500/50 text-rose-400'
            }`}
          >
            {toast.type === 'success' ? <CheckCircle2 size={24} /> : <AlertCircle size={24} />}
            <span className="font-bold tracking-wide">{toast.msg}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[400px] md:w-[600px] h-[400px] md:h-[600px] bg-blue-900/15 rounded-full blur-[100px] md:blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-purple-900/15 rounded-full blur-[100px] md:blur-[120px]" />
      </div>

      <div className="max-w-[1600px] w-full mx-auto px-4 sm:px-6 relative z-10">
        
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row justify-between items-start md:items-end mb-6 md:mb-8 gap-6 border-b border-gray-800/50 pb-6"
        >
          <div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight flex items-center gap-3">
              <Activity className="text-blue-500" /> Market Overview
            </h2>
            <p className="text-gray-400 mt-2 font-medium">Real-time analysis and execution for <span className="text-blue-400">Motilal Algo</span>.</p>
          </div>
          
          <div className="flex gap-6 text-sm font-medium w-full md:w-auto justify-between md:justify-end border-t md:border-t-0 border-gray-800/50 pt-4 md:pt-0">
             <div className="flex flex-col items-start md:items-end">
                <span className="text-gray-500 text-xs font-bold tracking-wider">NIFTY 50</span>
                <span className="text-emerald-400 flex items-center gap-1 font-mono text-base">
                   22,514.65 <ArrowUpRight size={16} />
                </span>
             </div>
             <div className="flex flex-col items-start md:items-end">
                <span className="text-gray-500 text-xs font-bold tracking-wider">BANKNIFTY</span>
                <span className="text-rose-400 flex items-center gap-1 font-mono text-base">
                  48,200.10 <ArrowDownRight size={16} />
                </span>
             </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[600px]">
          
          {/* Chart Area */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-8 xl:col-span-9 bg-[#0b1215] border border-gray-800 rounded-2xl overflow-hidden shadow-2xl relative flex flex-col h-[500px] lg:h-auto"
          >
             {/* Note the matching ID here */}
             <div id="tradingview_chart_container" ref={widgetContainerRef} className="w-full h-full" />
          </motion.div>

          {/* Interactive Side Panel */}
          {/* <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-4 xl:col-span-3 flex flex-col gap-6"
          >
            
            <div className="bg-gradient-to-br from-[#111827] to-[#0b1215] border border-gray-800 hover:border-gray-700 rounded-2xl p-6 shadow-xl relative overflow-hidden group transition-colors">
               <div className="absolute -top-4 -right-4 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                 <Wallet size={120} className="text-blue-500" />
               </div>
               
               <h3 className="text-sm font-bold text-gray-500 mb-1 tracking-wider uppercase">Funds Available</h3>
               <p className="text-3xl font-mono text-white mb-4 drop-shadow-md">
                 ₹{inrBalance.toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
               </p>
               
               <h3 className="text-xs font-bold text-gray-500 mb-1 tracking-wider uppercase">Holdings (RELIANCE)</h3>
               <p className="text-lg font-mono text-blue-400 mb-5">
                 {stockBalance} Shares
               </p>

               <div className="flex gap-3 relative z-10">
                 <button className="flex-1 bg-gray-800 hover:bg-gray-700 text-white text-xs font-bold py-2.5 rounded-lg transition-colors border border-gray-700">
                    ADD FUNDS
                 </button>
                 <button className="flex-1 bg-gray-800 hover:bg-gray-700 text-white text-xs font-bold py-2.5 rounded-lg transition-colors border border-gray-700">
                    WITHDRAW
                 </button>
               </div>
            </div>

            <div className="bg-[#111827] border border-gray-800 rounded-2xl p-6 shadow-xl flex-1 flex flex-col">
              <div className="flex items-center gap-2 mb-6">
                <Repeat className="text-blue-500" size={20} />
                <h3 className="text-lg font-extrabold text-white">Equity Delivery</h3>
              </div>

              <div className="flex bg-[#0b1215] p-1.5 rounded-xl mb-6 border border-gray-800">
                <button 
                  onClick={() => { setActiveTab('buy'); setQuantity(""); }}
                  className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${activeTab === 'buy' ? 'bg-emerald-500 text-white shadow-[0_0_15px_rgba(16,185,129,0.3)]' : 'text-gray-500 hover:text-gray-300'}`}
                >
                  BUY
                </button>
                <button 
                  onClick={() => { setActiveTab('sell'); setQuantity(""); }}
                  className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${activeTab === 'sell' ? 'bg-rose-500 text-white shadow-[0_0_15px_rgba(244,63,94,0.3)]' : 'text-gray-500 hover:text-gray-300'}`}
                >
                  SELL
                </button>
              </div>

              <div className="space-y-5 flex-1">
                <div>
                  <div className="flex justify-between mb-1.5 px-1">
                    <label className="text-[10px] sm:text-xs text-gray-500 font-bold tracking-wider">PRICE (INR)</label>
                    <label className="text-[10px] sm:text-xs text-gray-500 font-medium">Market</label>
                  </div>
                  <div className="relative">
                     <input 
                        type="text" 
                        value={price.toLocaleString('en-IN', {minimumFractionDigits: 2})}
                        readOnly
                        className="w-full bg-[#0b1215] border border-gray-800 text-gray-400 p-3.5 rounded-xl font-mono outline-none cursor-not-allowed"
                     />
                     <span className="absolute right-4 top-3.5 text-xs text-gray-600 font-bold">₹</span>
                  </div>
                </div>

                <div>
                   <div className="flex justify-between mb-1.5 px-1">
                     <label className="text-[10px] sm:text-xs text-gray-500 font-bold tracking-wider">QUANTITY</label>
                   </div>
                   <div className="relative group">
                     <input 
                        type="number" 
                        placeholder="0"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        className={`w-full bg-[#0b1215] border p-3.5 rounded-xl font-mono outline-none transition-colors text-white ${
                          activeTab === 'buy' ? 'focus:border-emerald-500/50 border-gray-700' : 'focus:border-rose-500/50 border-gray-700'
                        }`}
                     />
                     <span className="absolute right-4 top-3.5 text-xs text-gray-500 font-bold">Qty</span>
                   </div>
                </div>

                <div className="flex justify-between items-center py-2 px-1 border-t border-gray-800/50 mt-4">
                  <span className="text-xs text-gray-500 font-bold">MARGIN REQUIRED</span>
                  <span className="text-sm font-mono text-white">
                    ₹{totalInr.toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </span>
                </div>
              </div>

              <motion.button 
                onClick={handleTrade}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full mt-4 py-4 rounded-xl font-extrabold text-white transition-all flex justify-center items-center gap-2 ${
                  activeTab === 'buy' 
                    ? 'bg-emerald-600 hover:bg-emerald-500 shadow-[0_0_20px_rgba(16,185,129,0.3)]' 
                    : 'bg-rose-600 hover:bg-rose-500 shadow-[0_0_20px_rgba(244,63,94,0.3)]'
                }`}
              >
                {activeTab === 'buy' ? 'BUY RELIANCE' : 'SELL RELIANCE'}
              </motion.button>
            </div>

          </motion.div> */}


          <motion.div 
  initial={{ opacity: 0, x: 30 }}
  whileInView={{ opacity: 1, x: 0 }}
  transition={{ duration: 0.6, delay: 0.2 }}
  className="lg:col-span-4 xl:col-span-3 flex flex-col gap-6"
>

  <div className="bg-gradient-to-br from-[#111827] to-[#0b1215] border border-gray-800 rounded-2xl p-6 shadow-xl">

    <h2 className="text-xl font-extrabold text-white mb-3">
      Motilal Algo Trading
    </h2>

    <p className="text-gray-400 text-sm leading-relaxed mb-4">
      Algo trading is an automated trading system that executes orders based on predefined strategies. 
      It analyzes market conditions in real-time to deliver fast, precise, and emotion-free trading decisions.
    </p>

    <ul className="text-gray-400 text-sm space-y-2 mb-5">
      <li>⚡ High-speed execution</li>
      <li>📊 Data-driven decisions</li>
      <li>🤖 Emotion-free trading</li>
      <li>⏱️ 24/7 strategy monitoring</li>
    </ul>

    <div className="bg-[#0b1215] border border-gray-800 rounded-xl p-4 mb-4">
      <h3 className="text-sm font-bold text-yellow-500 mb-2">
        ⚠️ Risk Disclaimer
      </h3>
      <p className="text-xs text-gray-500 leading-relaxed">
        Trading in financial markets involves significant risk. Algo trading does not guarantee profits. 
        Please trade at your own risk after proper research and understanding. 
        Past performance is not indicative of future results.
      </p>
    </div>
     
      <Link to="/demat/register" className="w-full md:w-auto">
    <button className="w-full bg-blue-600 hover:bg-blue-500 text-white py-3 rounded-xl font-bold transition-all">
      START TRADING
    </button>
    </Link>

  </div>

</motion.div>
        </div>
      </div>
    </section>
  );
};

export default MarketOverview;